// Live data for /bitcoin-fees (BitcoinFees.now on the server, GET /api/bitcoin_fees).
export interface FeeTier {
  key: "fastest" | "half_hour" | "hour" | "economy";
  label: string;
  wait: string;
  rate: number; // sat/vB
}

export interface FeeCurrency {
  code: string;
  name: string;
  symbol: string;
  price: number; // 1 BTC in this currency
}

export interface FeeData {
  tiers: FeeTier[] | null;
  minimum: number | null;
  precise: boolean;
  fees_updated_at: number | null; // unix seconds
  currencies: FeeCurrency[];
  block_height: number | null;
  block_time: number | null; // unix seconds
  typical_vbytes: number;
  server_time: number;
  currency?: string;
}

export const fetchFeeData = async (): Promise<FeeData | null> => {
  try {
    const response = await fetch("/api/bitcoin_fees", { headers: { Accept: "application/json" } });
    if (!response.ok) return null;
    const data = (await response.json()) as FeeData;
    return data.tiers ? data : null;
  } catch (_e) {
    return null;
  }
};

const SATS_PER_BTC = 100_000_000;
const HALVING_INTERVAL = 210_000;

// Address types and their sizes in weight units (vbytes × 4). Inputs assume a single-key
// spend with a 72-byte signature; Taproot is a key-path spend.
export type ScriptType = "p2wpkh" | "p2tr" | "p2sh_p2wpkh" | "p2pkh";

export const SCRIPT_TYPES: { value: ScriptType; label: string; inputWu: number; outputWu: number; segwit: boolean }[] = [
  { value: "p2wpkh", label: "Native SegWit (bc1q…)", inputWu: 272, outputWu: 124, segwit: true },
  { value: "p2tr", label: "Taproot (bc1p…)", inputWu: 230, outputWu: 172, segwit: true },
  { value: "p2sh_p2wpkh", label: "Nested SegWit (3…)", inputWu: 364, outputWu: 128, segwit: true },
  { value: "p2pkh", label: "Legacy (1…)", inputWu: 592, outputWu: 136, segwit: false },
];

const varIntBytes = (n: number) => (n < 253 ? 1 : n <= 0xffff ? 3 : 5);

// Virtual size of a transaction with `inputs` and `outputs` of one address type.
// 1 input + 2 outputs of Native SegWit = 141 vB, Taproot 154, Nested 166, Legacy 226.
export const txVbytes = (type: ScriptType, inputs: number, outputs: number): number => {
  const script = SCRIPT_TYPES.find((s) => s.value === type) ?? SCRIPT_TYPES[0];
  // version (4) + locktime (4) + input and output counts; SegWit adds marker + flag (2 WU)
  const overheadWu = (8 + varIntBytes(inputs) + varIntBytes(outputs)) * 4 + (script.segwit ? 2 : 0);
  return Math.ceil((overheadWu + inputs * script.inputWu + outputs * script.outputWu) / 4);
};

// sats for `vbytes` at `rate` sat/vB, rounded up like wallets do (matches BitcoinFees.sats_for)
export const satsFor = (rate: number, vbytes: number) => Math.ceil(rate * vbytes);

const groupThousands = (whole: string) => whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

export const formatSats = (sats: number) => groupThousands(String(Math.round(sats)));

// Same rules as ConverterFormatHelper#converter_money (English separators):
// 83,193 · 83.19 · 0.832 · 0.000832
export const formatMoney = (value: number, symbol: string, fixedDigits?: number): string => {
  const digits =
    fixedDigits ?? (value >= 1000 ? 0 : value >= 1 || value === 0 ? 2 : 2 - Math.floor(Math.log10(value)));
  const [whole, fraction] = value.toFixed(digits).split(".");
  const number = fraction ? `${groupThousands(whole)}.${fraction}` : groupThousands(whole);
  return `${symbol}${symbol.length > 1 ? " " : ""}${number}`;
};

// Fee money, like BitcoinFees.money: cents from 0.10 up ("$0.41", not "$0.405")
export const satsToMoney = (sats: number, currency: FeeCurrency) => {
  const value = (sats * currency.price) / SATS_PER_BTC;
  return formatMoney(value, currency.symbol, value >= 0.1 && value < 1000 ? 2 : undefined);
};

// Same as BitcoinFees.rate_label: "2.52", "0.2", "14.5", "120"
export const rateLabel = (rate: number): string => {
  if (rate >= 100) return String(Math.round(rate));
  return String(Number(rate.toFixed(rate >= 10 ? 1 : 2)));
};

// Same bands as BitcoinFees::BANDS (fixed thresholds on the next-block rate)
export const BANDS: { below: number; label: string; color: string }[] = [
  { below: 1, label: "Very low", color: "teal" },
  { below: 5, label: "Low", color: "green" },
  { below: 20, label: "Normal", color: "yellow" },
  { below: 50, label: "Busy", color: "orange" },
  { below: Infinity, label: "Very busy", color: "red" },
];

export const bandFor = (rate: number) => BANDS.find((band) => rate < band.below) ?? BANDS[BANDS.length - 1];

export const nextHalving = (height: number) => {
  const target = (Math.floor(height / HALVING_INTERVAL) + 1) * HALVING_INTERVAL;
  const blocksLeft = target - height;
  return { height: target, blocksLeft, estimatedAt: new Date(Date.now() + blocksLeft * 600_000) };
};

// "just now", "45 s ago", "12 min ago", "2 h 5 min ago"
export const agoLabel = (seconds: number): string => {
  const s = Math.max(0, Math.round(seconds));
  if (s < 5) return "just now";
  if (s < 60) return `${s} s ago`;
  const minutes = Math.floor(s / 60);
  if (minutes < 60) return `${minutes} min ago`;
  return `${Math.floor(minutes / 60)} h ${minutes % 60} min ago`;
};
